import { HStack, Text } from '@chakra-ui/react';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { FundingRequestStatusEnumGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { getDecodedTokenOrThrow } from '~/contexts/UserAuthProvider';

// Types
type Status = 'info' | 'success' | 'warning' | 'error';

// Helpers
const getColor = (status?: Status): { 100: string; 400: string } => {
  switch (status) {
    case 'info':
      return colors.blue;
    case 'success':
      return colors.green;
    case 'warning':
      return colors.orange;
    case 'error':
      return colors.red;
    default:
      return colors.gray;
  }
};

// Exports
type StatusBadgeProps = { icon: string; status?: Status; label: string };
export const StatusBadge = ({ status, label, icon }: StatusBadgeProps) => (
  <HStack
    bgColor={getColor(status)[100]}
    p="8px 16px"
    spacing="16px"
    borderRadius="8px"
  >
    <Icon icon={icon} color={getColor(status)[400]} />
    <Text color={getColor(status)[400]} fontWeight="bold">
      {label}
    </Text>
  </HStack>
);

type Props = { status: FundingRequestStatusEnumGQLType };
export const FundingRequestStatusBadge = ({ status }: Props) => {
  const userType = getDecodedTokenOrThrow().userType;
  switch (status) {
    case 'unsubmitted':
    case 'pending':
      return <StatusBadge icon="mailbox" label="Not Submitted" />;
    case 'submitted':
      return (
        <StatusBadge
          status="info"
          icon={userType === 'BORROWER' ? 'mailbox-flag-up' : 'circle-check'}
          label={userType === 'BORROWER' ? 'Submitted' : 'Ready for Review'}
        />
      );
    case 'expired':
      return <StatusBadge icon="calendar-xmark" label="Expired" />;
    case 'canceled':
      return <StatusBadge icon="circle-x" label="Canceled" />;
    case 'rejected':
      return <StatusBadge status="error" icon="circle-x" label="Rejected" />;
    case 'approved':
      return (
        <StatusBadge status="success" icon="circle-check" label="Approved" />
      );
    default:
      return finleyAssertUnreachable(status);
  }
};

export const statusConfigs: Record<
  FundingRequestStatusEnumGQLType,
  { bg: string; color: string; label: string }
> = {
  approved: {
    bg: 'green.100',
    color: 'green.400',
    label: 'Approved',
  },
  submitted: {
    bg: 'blue.100',
    color: 'blue.400',
    label: 'Submitted',
  },
  pending: {
    bg: 'gray.100',
    color: 'gray.900',
    label: 'Pending',
  },
  canceled: {
    bg: 'gray.100',
    color: 'gray.900',
    label: 'Canceled',
  },
  unsubmitted: {
    bg: 'gray.100',
    color: 'gray.900',
    label: 'Unsubmitted',
  },
  expired: { bg: 'red.100', color: 'red.400', label: 'Expired' },
  rejected: {
    bg: 'red.100',
    color: 'red.400',
    label: 'Rejected',
  },
};
